import { Button, Card, Col, Form, Input, Row, Table, Typography, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React, { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import ViewBtn from "../Buttons/ViewBtn";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import { addRole, getRoles } from "./roleApis";

function CustomTable({ list, onEdit }) {
  const [columnsToShow, setColumnsToShow] = useState([]);

  const columns = [
    {
      id: 1,
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
    },
    {
      id: 2,
      title: "Name",
      dataIndex: "RoleName",
      key: "RoleName",
      render: (roleName) => roleName || "N/A", // Fallback for missing RoleName
    },
    {
      id: 3,
      title: "Action",
      dataIndex: "RoleId",
      key: "action",
      render: (roleId, record) => {
        // console.log("Action Column - roleId:", roleId, "record:", record); // Debug log
        return (
          <Space>
            <ViewBtn path={`/admin/role/${record.RoleId}`} />
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) =>
    arr.map((i, index) => ({
      ...i,
      key: i.RoleId || `key-${index}`, // Fallback key if RoleId is missing
      serial: index + 1,
    }));

  return (
    <div className="ant-card p-4 rounded role-table-container">
      <div className="text-center my-2 flex justify-between">
        <h5 className="text-xl role-list-title mb-2">Role List</h5>
        {list && list.length > 0 && (
          <div>
            <CsvLinkBtn>
              <CSVLink
                data={list}
                className="btn btn-dark btn-sm mb-1"
                filename="roles"
              >
                Download CSV
              </CSVLink>
            </CsvLinkBtn>
          </div>
        )}
      </div>

      {list && (
        <div style={{ marginBottom: "30px" }}>
          <ColVisibilityDropdown
            options={columns}
            columns={columns}
            columnsToShowHandler={columnsToShowHandler}
          />
        </div>
      )}

        <Table
          scroll={{ x: true, y: "100%" }}
          loading={!list}
          columns={columnsToShow}
          dataSource={list ? addKeys(list) : []}
          rowKey={(record) => record.key} // Use key for unique row identifier
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        />
      </div>
  );
}

const AddRole = ({ drawer }) => {
  const [list, setList] = useState(null);
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);

  useEffect(() => {
    getRoles()
      .then((d) => {
        // console.log("Fetched roles:", d); // Debug: Log fetched roles
        setList(Array.isArray(d) ? d : []); // Ensure list is an array
      })
      .catch((error) => {
        // console.log("Error fetching roles:", error);
        setList([]); // Set empty array on error
      });
  }, []);

  const handleEdit = (record) => {
    // console.log("Editing record:", record); // Debug: Log the record being edited
    setIsEditMode(true);
    const roleId = record.RoleId || record.id; // Fallback to id if RoleId is missing
    // console.log("Setting currentRoleId to:", roleId); // Debug: Log the ID being set
    setCurrentRoleId(roleId);
    form.setFieldsValue({
      name: record.RoleName || "", // Fallback to empty string
    });
  };

  const resetForm = () => {
    form.resetFields();
    setIsEditMode(false);
    setCurrentRoleId(null);
  };

  const onFinish = async (values) => {
    setLoader(true);
    try {
      const roleIdToUse = isEditMode && currentRoleId ? currentRoleId : 0;

      const roleData = {
        RoleId: roleIdToUse,
        RoleName: values.name,
        DataStatus: 1,
      };

      // console.log("Sending roleData:", roleData); // Debug: Log the payload

      const resp = await addRole(roleData);

      if (resp.message === "success") {
        const updatedList = await getRoles();
        setList(Array.isArray(updatedList) ? updatedList : []);
        resetForm();
        toast.success(
          isEditMode ? "Role updated successfully!" : "Role added successfully!"
        );
      } else {
        toast.error(
          isEditMode ? "Failed to update role" : "Failed to add role"
        );
      }
    } catch (error) {
      // console.error("Error in onFinish:", error);
      toast.error(
        isEditMode ? "Failed to update role" : "Failed to add role"
      );
    } finally {
      setLoader(false);
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    const errorMessages = errorFields
      .map((field) => field.errors.join(", "))
      .join("; ");
    toast.warning(`Please fix the following errors: ${errorMessages}`);
    setLoader(false);
  };

  const { Title } = Typography;

  return (
    <div className="add-role-page-container">
      <Row className="add-role-row" gutter={[16, 16]}>
        <Col xs={24} sm={24} md={10} lg={8} xl={6} className="add-role-form-col">
          <Card className="border rounded card-custom add-role-form-card">
            <Title level={4} className="text-center">
              {isEditMode ? "Edit Role" : "Add New Role"}
            </Title>
            <Form
              form={form}
              name="basic"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 16 }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label={<span className="required-label">Name</span>}
                name="name"
                rules={[{ required: true, message: "Please input role name!" }]}
              >
                <Input placeholder="e.g., Test-Administrator" />
              </Form.Item>

              <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    block
                    loading={loader}
                  >
                    {isEditMode ? "Update Role" : "Add New Role"}
                  </Button>
                  {isEditMode && (
                    <Button size="large" onClick={resetForm}>
                      Cancel
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} sm={24} md={14} lg={16} xl={18} className="add-role-table-col">
          <CustomTable list={list} onEdit={handleEdit} />
        </Col>
      </Row>
    </div>
  );
};

export default AddRole;